/* eslint-disable prefer-template */
import './SurveyBuilderQuestionAccordion.scss'
import React, { Fragment } from 'react'
import { observer } from 'mobx-react'
import {
    makeObservable, observable, action, computed,
} from 'mobx'
import AnimateHeight from 'react-animate-height'
import i18n from 'i18next'
import * as R from 'ramda'

import GrabIcon from '../../../assets/images/move-icon.svg'
import Menu from '../../../UniformComponents/Menu'
import HoverInfo from '../../../UniformComponents/HoverInfo'
import Tooltip from '../../../UniformComponents/Tooltip'
import Button from '../../../UniformComponents/Button'
import Typography from '../../../UniformComponents/Typography'
import Checkbox from '../../form/Checkbox'
import Select from '../../form/Select'
import DebugInfo from '../../ReportV2/components/RightPanel/DebugInfo'
import { getQuestionById } from '../../../utils/QuestionHelper'

import * as SkipLogicPreview from '../../SkipLogic/Preview'

const menuActions = Object.freeze({
    REQUIRED: 'required',
    COPY: 'copyQuestion',
    EDIT: 'editQuestion',
    SKIP: 'skip',
    LOCK: 'lock',
    SHOW: 'show',
    INHERIT: 'inherit',
    DELETE: 'delele',
    MAP: 'map',
    REMOVE_AI: 'removeAi',
})

@observer
export default class SurveyBuilderQuestionAccordion extends React.Component {
    @observable open = false

    @observable menuOpen = false

    @observable openTab = null

    qListInfo = {}

    parentPageQuestionsIds = new Set()

    hiddenPageQuestionsIds = new Set()

    constructor(props) {
        super(props)
        makeObservable(this)
        this.setRef()
        this.getQListInfo()
    }

    componentDidUpdate(prevProps) {
        const { isLite, isFailed, isDragging } = this.props
        if (isLite || isFailed) return
        if (isDragging && !prevProps.isDragging && this.open) {
            this.togglePreview()
        }
    }

    @action
    setRef = () => {
        const { question, store, isLite } = this.props
        if (isLite) return // isLite does not have store
        store.iframeRefs.set(question.id, {
            ref: React.createRef(),
            height: 'auto',
        })
    }

    @computed
    get getAllSurveyQuestionIds() {
        const { store } = this.props
        if (!store || !store.survey) return []
        return store.survey.pages
            .filter((pq) => pq.pageType === 'ST')
            .flatMap((pq) => pq.questions)
            .map((q) => q.questionId ?? undefined)
    }

    @computed
    get isAiParent() {
        let res = false
        const { store, question } = this.props
        if (store?.surveyAiQuestionMapping && question) {
            const mapValues = [...store.surveyAiQuestionMapping.values()]
            res = mapValues.includes(question.questionId)
        }
        return res
    }

    getQListInfo = () => {
        const info = {
            mandatory: {
                isMandatory: false,
                menuTooltipText: '',
                toolbarTooltipText: '',
            },
            recommended: {
                isRecommended: false,
                toolbarTooltipText: '',
            },
        }
        const { question } = this.props
        const { questionId } = question
        const { store } = this.props
        if (!store?.survey?.questionLibrary) return info
        const { questionLibraryStore } = store.survey.questionLibrary

        const mandatoryQuestions = questionLibraryStore.questionLibraryLists.get('mandatory')
        const isMandatory = mandatoryQuestions.find(
            (mq) => mq.questionId === questionId.toUpperCase(),
        )
        if (isMandatory) {
            info.mandatory.isMandatory = true
            const text = i18n.t(
                'questionLibrary.question.option.infoText.disabled_mandatory',
                {
                    productType: isMandatory.producttypename,
                    respondentType: isMandatory.respondenttypename,
                },
            )
            info.mandatory.menuTooltipText = text
            info.mandatory.toolbarTooltipText = text
        }

        const recommendedQuestions = questionLibraryStore.questionLibraryLists.get('recommended')
        const isRecommended = recommendedQuestions.find(
            (mq) => mq.questionId === questionId.toUpperCase(),
        )
        if (isRecommended) {
            info.recommended.isRecommended = true
            info.recommended.menuTooltipText = i18n.t('questionLibrary.question.option.recommended')
        }
        this.qListInfo = info
    }

    getUpdatedSurveyInfo = () => {
        const { store } = this.props
        const { survey } = store
        if (!survey) return []

        const pageQuestions = survey.pages.flatMap((p) => p.questions)
        const questionData = pageQuestions.reduce((ids, pq) => {
            if (!pq) return ids
            if (pq.parentId) ids.parentPageQuestionsIds.add(pq.parentId)
            if (pq.isHidden) ids.hiddenPageQuestionsIds.add(pq.id)
            if (pq.populateFrom) {
                const defaultLocale = R.pathOr('', ['survey', 'metadata', 'defaultLocale'], store)
                const shortTitle = pq.question.content.getTitleByLocale('shortTitle', store.surveyLocale, defaultLocale)
                ids.mappedQuestionsIds.set(
                    pq.questionId,
                    shortTitle,
                )
            }
            return ids
        }, {
            parentPageQuestionsIds: new Set(), // questions providing answer options to other questions in this survey
            hiddenPageQuestionsIds: new Set(),
            mappedQuestionsIds: new Map(),
        })

        this.parentPageQuestionsIds = questionData.parentPageQuestionsIds
        this.hiddenPageQuestionsIds = questionData.hiddenPageQuestionsIds
        store.surveyQuestionsIdsWithMapping = questionData.mappedQuestionsIds
    }

    @action
    togglePreview = () => {
        if (this.open && this.openTab === 'preview') {
            this.openTab = null
            this.open = false
        } else {
            const { store, question, isLite } = this.props
            if (isLite) return
            const iframeRef = store.iframeRefs.get(question.id)
            store.iframeRefs.set(question.id, {
                ref: iframeRef.ref,
                height: 'auto',
            })
            this.openTab = 'preview'
            this.open = true
        }
    }

    setOpenTab = (tab) => {
        if (this.openTab === tab && this.open) {
            this.open = false
            this.openTab = null
            return
        }

        this.openTab = tab
        if (!this.open) this.open = true
    }

    previewLink = () => {
        const { store, question, page } = this.props
        const { survey, useLatestUI } = store
        const prot = window.location.protocol
        console.log(`question previewLink ${useLatestUI}`)
        let url = window.location.host.split('.')
        if (url[0].match(/dev/)) {
            url[0] = 'surveys.staging'
        } else if (url.includes('staging') && url[0] !== 'admin') {
            url.splice(0, 2, 'surveys')
        } else {
            url[0] = 'surveys'
        }
        url = url.join('.')
        return `${prot}//${url}/?surveyUuid=${survey.publicId}&pageId=${page.id}&questionId=${question.id}${useLatestUI ? '&version=v2' : ''}&langcode=${store.surveyLocale}`
    }

    renderContent = () => {
        const { question, store, inheritParent } = this.props
        const iframeRef = !store ? null : store.iframeRefs.get(question.id)
        const locale = R.pathOr('', ['surveyLocale'], store)
        const defaultLocale = R.pathOr('', ['survey', 'metadata', 'defaultLocale'], store)
        const locales = {
            locale,
            defaultLocale,
        }

        switch (this.openTab) {
            case 'preview':
                return (
                    <object
                        ref={iframeRef}
                        style={{
                            width: '100%',
                            marginLeft: '0em',
                            height: iframeRef ? iframeRef.height : 0,
                            maxHeight: 450,
                        }}
                        data={this.previewLink()}
                        aria-label="question preview"
                    />
                )
            case 'option':
                return SkipLogicPreview.showOption(store.survey, question, locales)
            case 'page':
                return SkipLogicPreview.skipToPage(store.survey, question, locales)
            case 'question':
                return SkipLogicPreview.showQuestion(store.survey, question, locales)
            case 'inherited':
                return (
                    <div>
                        <div className="printShowIf survey-builder-option-tab-options">
                            <span>
                                {i18n.t('s_answer_selected')}
                            </span>

                            <span style={{ fontWeight: 'bold' }}>
                                {inheritParent.label}
                            </span>

                            <span>
                                {i18n.t('s_will_be_inherited')}
                            </span>
                        </div>
                    </div>
                )
            case 'mapping': {
                const { survey } = store
                const mappedFromId = question.populateFrom.pageQuestionId
                if (!mappedFromId) return null
                const mappedFromQuestion = survey.pages
                    .flatMap((p) => p.questions ?? [])
                    .find((pq) => pq.id === mappedFromId)
                if (!mappedFromQuestion) return null
                const questionTitle = mappedFromQuestion.question.content
                    .getTitleByLocale('shortTitle', locale, defaultLocale)
                return (
                    <div className="printShowIf survey-builder-option-tab-options">
                        <p>
                            {i18n.t('s_mapping_answers_from_pt1')}
                            {' '}
                            <b>{questionTitle}</b>
                            {' '}
                            {i18n.t('s_mapping_answers_from_pt2')}
                        </p>
                    </div>
                )
            }
            case 'aiMapping':
                return (
                    <div className="printShowIf survey-builder-option-tab-options">
                        <span>{i18n.t('s_ai_categorisation_desc')}</span>
                    </div>
                )
            default:
                return null
        }
    }

    toggleMenu = (val) => {
        if (val) this.menuOpen = val
        else this.menuOpen = !this.menuOpen
    }

    execMenuAction = (func) => {
        this.toggleMenu(false)
        this.openTab = ''
        func()
    }

    shouldHideOptions = (store, key, question) => {
        // hide options for company and explori level questions (except show/hide question) on Lite surveys
        const questionLevel = R.pathOr(1, ['question', 'metadata', 'oid'], question)
        return questionLevel <= 3
            && ![menuActions.SHOW, menuActions.DELETE].includes(key)
            && store.survey.metadata.isRapidSurvey
    }

    hideMenuItem = (item) => {
        item.isVisible = false
        item.isDisabled = true
    }

    getMenuItemConfig = (key, question) => {
        const { store } = this.props
        const { survey } = store
        const item = {
            isVisible: true, isDisabled: false, tooltip: '', disabledReasons: [], rapidDisabled: false,
        }
        const hideOptions = this.shouldHideOptions(store, key, question)
        if (hideOptions) {
            this.hideMenuItem(item)
            return item
        }
        const isSurveyClosed = store && store.survey.closed
        const isLocked = !question?.canBeEdited
        const prefix = 'questionLibrary.question.option.infoText.'

        switch (key) {
            case menuActions.REQUIRED: {
                if (isLocked) {
                    item.disabledReasons.push(i18n.t(prefix + 'disabled_locked'))
                    item.isDisabled = true
                }
                const { mandatory } = this.qListInfo
                if (mandatory.isMandatory) {
                    item.disabledReasons.push(mandatory.menuTooltipText)
                    item.isDisabled = true
                }

                if (survey.metadata.isRapidSurvey) {
                    item.disabledReasons.push(
                        i18n.t('questionLibrary.question.option.infoText.required.rapid_disabled'),
                    )
                    item.isDisabled = true
                    item.rapidDisabled = true
                }

                if (item.isDisabled) {
                    item.tooltip = i18n.t(prefix + 'required.disabled_desc')
                } else {
                    item.tooltip = i18n.t(prefix + 'required')
                }
                if (isSurveyClosed) this.hideMenuItem(item)
                return item
            }
            case menuActions.LOCK: {
                if (store.isMasterSuper()) {
                    if (isLocked) item.tooltip = i18n.t(prefix + 'lock.locked.master')
                    else item.tooltip = i18n.t(prefix + 'lock.unlocked.master')
                } else if (isLocked) {
                    item.tooltip = (i18n.t(prefix + 'lock.locked'))
                    item.isDisabled = true
                } else {
                    item.isVisible = false
                }
                if (isSurveyClosed) this.hideMenuItem(item)
                return item
            }
            case menuActions.EDIT: {
                // previous rule
                // item.isVisible = question.isEditable && question.modifiable && question.deletable
                const { isEditable, editMode } = question
                if (!isEditable || editMode === 'None') {
                    item.disabledReasons.push(i18n.t(prefix + 'edit.disabled_core'))
                    item.isDisabled = true
                }
                if (isLocked) {
                    item.disabledReasons.push(i18n.t(prefix + 'disabled_locked'))
                    item.isDisabled = true
                }
                const { mandatory } = this.qListInfo
                if (mandatory.isMandatory) {
                    item.disabledReasons.push(mandatory.menuTooltipText)
                    item.isDisabled = true
                }

                if (survey.metadata.isRapidSurvey && R.pathOr(0, ['question', 'metadata', 'oid'], question) !== 6) {
                    item.disabledReasons.push(
                        i18n.t('questionLibrary.question.option.infoText.generic.rapid_disabled'),
                    )
                    item.isDisabled = true
                    item.rapidDisabled = true
                }

                // end
                if (item.isDisabled) {
                    item.tooltip = i18n.t(prefix + 'edit.disabled_desc')
                }
                if (isSurveyClosed) this.hideMenuItem(item)
                return item
            }
            case menuActions.DELETE: {
                if (isLocked) {
                    item.disabledReasons.push(i18n.t(prefix + 'disabled_locked'))
                    item.isDisabled = true
                }
                const { mandatory } = this.qListInfo
                if (mandatory.isMandatory) {
                    item.disabledReasons.push(mandatory.menuTooltipText)
                    item.isDisabled = true
                }
                // COMMENTING OUT INCASE WE WANT TO REVERT LATER
                // if (survey.metadata.isRapidSurvey) {
                //     item.disabledReasons.push(
                //         i18n.t('questionLibrary.question.option.infoText.delete.rapid_disabled'),
                //     )
                //     item.isDisabled = true
                //     item.rapidDisabled = true
                // }
                if (this.getAiParentInfo().isAiParentInSurvey) {
                    item.disabledReasons.push(i18n.t(prefix + 'disabled_usingAI'))
                    item.isDisabled = true
                }
                if (item.isDisabled && !isSurveyClosed) {
                    item.tooltip = i18n.t(prefix + 'delete.disabled_desc')
                }
                if (isSurveyClosed) item.isDisabled = true

                return item
            }
            case menuActions.SKIP: {
                if (isLocked) {
                    item.disabledReasons.push(i18n.t(prefix + 'disabled_locked'))
                    item.isDisabled = true
                }
                if (survey.metadata.isRapidSurvey) {
                    item.disabledReasons.push(
                        i18n.t('questionLibrary.question.option.infoText.generic.rapid_disabled'),
                    )
                    item.isDisabled = true
                    item.rapidDisabled = true
                }
                if (item.isDisabled) {
                    item.tooltip = i18n.t(prefix + 'skiplogic.disabled_desc')
                } else {
                    item.tooltip = i18n.t(prefix + 'skiplogic.desc')
                }
                if (isSurveyClosed) this.hideMenuItem(item)
                return item
            }
            case menuActions.SHOW: {
                if (isLocked) {
                    item.tooltip = i18n.t(prefix + 'show.disabled_desc')
                    item.disabledReasons.push(i18n.t(prefix + 'disabled_locked'))
                    item.isDisabled = true
                }
                if (isSurveyClosed) this.hideMenuItem(item)
                return item
            }
            case menuActions.INHERIT: {
                const { inheritsAllowed } = this.props
                if (isLocked) {
                    item.disabledReasons.push(i18n.t(prefix + 'disabled_locked'))
                    item.isDisabled = true
                }
                const { mandatory } = this.qListInfo
                if (mandatory.isMandatory) {
                    item.disabledReasons.push(mandatory.menuTooltipText)
                    item.isDisabled = true
                }

                if (survey.metadata.isRapidSurvey) {
                    item.disabledReasons.push(
                        i18n.t('questionLibrary.question.option.infoText.generic.rapid_disabled'),
                    )
                    item.isDisabled = true
                    item.rapidDisabled = true
                }

                // target question type check
                const validQuestionTypes = inheritsAllowed.to
                if (!validQuestionTypes.includes(question.question?.questionTypeCode)) {
                    item.disabledReasons.push(i18n.t(prefix + 'inherit.disabled_qtype'))
                    item.isDisabled = true
                }
                // prev questions type check
                const pageQuestion = store.survey?.pages.find(
                    (p) => p.questions.some((q) => q.questionId === question.questionId),
                )
                const prevTypes = new Set(pageQuestion.prev_questions.map((q) => q.type))
                const arePrevQuestionsValid = validQuestionTypes.some((type) => prevTypes.has(type))
                if (!arePrevQuestionsValid) {
                    item.disabledReasons.push(i18n.t(prefix + 'inherit.disabled_na_questions'))
                    item.isDisabled = true
                }
                if (item.isDisabled) {
                    item.tooltip = i18n.t(prefix + 'inherit.disabled_desc')
                } else {
                    item.tooltip = i18n.t(prefix + 'inherit.enabled_desc')
                }
                if (isSurveyClosed) this.hideMenuItem(item)
                return item
            }
            case menuActions.COPY: {
                if (survey.metadata.isRapidSurvey) {
                    item.disabledReasons.push(
                        i18n.t('questionLibrary.question.option.infoText.generic.rapid_disabled'),
                    )
                    item.isDisabled = true
                    item.rapidDisabled = true
                }

                if (item.isDisabled) {
                    item.tooltip = i18n.t('questionLibrary.question.option.infoText.copy.disabled_desc')
                }

                return item
            }
            case menuActions.MAP: {
                const { isHidden } = question
                const { mapsAllowed } = this.props
                if (!isHidden) {
                    item.isDisabled = true
                    item.isVisible = false
                }
                if (this.parentPageQuestionsIds.has(question.id)) {
                    item.isDisabled = true
                    item.disabledReasons.push(i18n.t(prefix + 'addMapping.disabled.provides_answer_options'))
                }
                if (!R.isNil(question.parentId)) {
                    item.isDisabled = true
                    item.disabledReasons.push(i18n.t(prefix + 'addMapping.disabled.child_question'))
                }
                if (survey.metadata.isRapidSurvey) {
                    item.disabledReasons.push(
                        i18n.t('questionLibrary.question.option.infoText.generic.rapid_disabled'),
                    )
                    item.isDisabled = true
                    item.rapidDisabled = true
                }
                const validQuestionTypes = mapsAllowed[question.question?.questionTypeCode]
                if (!validQuestionTypes) {
                    // means this question does not accept mapping
                    item.isVisible = false
                } else {
                    const pageIndex = store.survey?.pages.findIndex(
                        (p) => p.questions.some((q) => q.questionId === question.questionId),
                    )
                    const pagesBefore = pageIndex > 0 ? store.survey.pages.slice(0, pageIndex) : []
                    const allPrevPageQuestions = pagesBefore.flatMap((page) => page.questions)
                    const currPage = store.survey.pages[pageIndex]
                    currPage.questions.some((pageQuestion) => {
                        if (pageQuestion.questionId !== question.questionId) {
                            allPrevPageQuestions.unshift(pageQuestion)
                            return false
                        }
                        return true // exit loop after current question is found on page
                    })
                    const prevTypes = new Set(allPrevPageQuestions.map((q) => q.question?.questionTypeCode))
                    const arePrevQuestionsValid = validQuestionTypes.some((type) => prevTypes.has(type))
                    if (!arePrevQuestionsValid) {
                        // means this question does not accept mapping from previous question types
                        item.disabledReasons.push(i18n.t(prefix + 'addMapping.disabled.na_questions'))
                        item.isDisabled = true
                    }
                }
                if (item.isDisabled) item.tooltip = i18n.t(prefix + 'addMapping.disabled_desc')
                else item.tooltip = i18n.t(prefix + 'addMapping.enabled_desc')
                return item
            }
            case menuActions.REMOVE_AI: {
                const { aiParentId, isAiParentInSurvey } = this.getAiParentInfo()
                if (!aiParentId || !isAiParentInSurvey) item.isVisible = false
                if (isSurveyClosed) this.hideMenuItem(item)
                return item
            }
            case menuActions.COPY: {
                if (isSurveyClosed) this.hideMenuItem(item)
                return item
            }
            default:
                return item
        }
    }

    createBaseMenu = () => {
        const {
            question,
            store,
            edit,
            copy,
            remove,
            makeMandatory,
            skip,
            lock,
            disable,
            inheritValue,
            inheritOptions,
            setInherit,
            pipeQuestion,
        } = this.props

        const menuList = (
            [
                {
                    compName: menuActions.EDIT,
                    icon: 'fa-light fa-pen-to-square',
                    label: i18n.t('edit_question'),
                    onClick: () => this.execMenuAction(edit),
                },
                {
                    compName: menuActions.COPY,
                    icon: 'fa-light fa-copy',
                    label: i18n.t('questionBuilder.optionsMenu.copy'),
                    onClick: () => this.execMenuAction(copy),
                },
                {
                    compName: menuActions.REMOVE_AI,
                    icon: 'fa-light fa-sparkles',
                    label: i18n.t('questionBuilder.optionsMenu.removeAi'),
                    onClick: () => this.execMenuAction(() => this.removeAiParent()),
                },
                {
                    compName: menuActions.REQUIRED,
                    icon: 'fa-light fa-circle-star',
                    label: i18n.t('questionBuilder.optionsMenu.required'),
                    menuType: 'checkbox',
                    value: question.isMandatory,
                    onClick: () => this.execMenuAction(makeMandatory),
                },
                {
                    compName: menuActions.SKIP,
                    icon: 'fa-light fa-arrows-repeat',
                    label: i18n.t('questionBuilder.optionsMenu.addSkipLogic'),
                    onClick: () => this.execMenuAction(skip),
                },
                {
                    compName: menuActions.LOCK,
                    icon: question.canBeEdited ? 'fal fa-user-lock' : 'fas fa-user-lock',
                    label: store.isMasterSuper()
                        ? i18n.t(question.canBeEdited ? 's_alt_q_lock' : 's_alt_q_unlock')
                        : i18n.t('s_alt_q_islocked'),
                    onClick: () => this.execMenuAction(lock),
                },
                {
                    compName: menuActions.SHOW,
                    icon: question.isHidden ? 'fa-light fa-eye' : 'fa-light fa-eye-slash',
                    label: i18n.t(question.isHidden ? 's_alt_q_show' : 's_alt_q_hide'),
                    onClick: () => this.execMenuAction(disable),
                },
                {
                    compName: menuActions.MAP,
                    icon: 'fa-light fa-map',
                    label: i18n.t('questionLibrary.question.option.infoText.addMapping.label'),
                    onClick: () => this.execMenuAction(pipeQuestion),
                },
                {
                    compName: menuActions.INHERIT,
                    icon: 'fa-light fa-diagram-project',
                    label: i18n.t('questionLibrary.question.option.inherit'),
                    menuType: 'menu',
                    list: [
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div className="inherit-question-title">
                                <p>
                                    {i18n.t('questionLibrary.question.option.subMenuTitle.inherit')}
                                </p>
                            </div>
                            <Select
                                placeholder={i18n.t('questionLibrary.question.option.inheritPlaceholder')}
                                name="parentQuestion"
                                label={i18n.t('questionLibrary.question.option.inheritLabel')}
                                onBlur={setInherit}
                                value={inheritOptions.find((o) => o.value === inheritValue)}
                                options={inheritOptions
                                    .filter((o) => !this.hiddenPageQuestionsIds.has(o.value))}
                            />
                        </div>,
                        <div>
                            <div
                                className="option-line"
                                style={{ display: !inheritValue ? 'none' : undefined }}
                                onClick={() => {
                                    setInherit({ value: null })
                                    question.refresh(store)
                                }}
                            >
                                <i className="fa-light fa-circle-xmark" />
                                <p dangerouslySetInnerHTML={{ __html: i18n.t('remove') }} />
                            </div>
                        </div>,
                    ],
                    iconShow: true,
                },
                {
                    compName: menuActions.DELETE,
                    label: i18n.t('del_question'),
                    icon: 'fa-light fa-circle-xmark',
                    greyBg: true,
                    onClick: () => this.execMenuAction(remove),
                },
            ]
        )
        return menuList
    }

    addUILogic = (menuItem) => {
        const { question } = this.props
        const item = this.getMenuItemConfig(menuItem.compName, question)
        return Object.assign({}, menuItem, {
            disabled: item.isDisabled,
            menuShow: item.isVisible,
            rapidDisabled: item.rapidDisabled,
            menuTooltip: item.disabledReasons.length === 0
                ? item.tooltip
                : `${item.tooltip}<br><ul>${item.disabledReasons.map((reason) => `<li>${reason}</li>`).join('')}</ul>`,
        })
    }

    getMenu = () => {
        this.getUpdatedSurveyInfo()
        const menuOptions = this.createBaseMenu().map((opt) => this.addUILogic(opt))
            .filter((config) => config.menuShow)
        return (
            menuOptions.map((config) => {
                switch (config.menuType) {
                    case 'checkbox':
                        return (
                            config.rapidDisabled
                                ? (
                                    <div
                                        className="option-line disabled"
                                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                    >
                                        <div className="option-line">
                                            <i className={config.icon} />
                                            <p dangerouslySetInnerHTML={{ __html: i18n.t(config.label) }} />
                                            {config.menuTooltip && (
                                                <HoverInfo
                                                    cssClass="hover-info"
                                                    title={config.menuTooltipTitle}
                                                    desc={config.menuTooltip}
                                                    dir="bottom"
                                                >
                                                    <i className="info-icon fa-solid fa-circle-question" />
                                                </HoverInfo>
                                            )}
                                        </div>

                                        <i className="far fa-lock-keyhole" style={{ fontSize: 16 }} />
                                    </div>
                                ) : (
                                    <div className={`option-line ${config.disabled ? 'disabled' : ''}`}>
                                        <i className={config.icon} />
                                        <Checkbox
                                            name={config.label}
                                            label={config.label}
                                            labelDir="left"
                                            cssClass="option-check"
                                            tooltipTitle={config.menuTooltipTitle}
                                            tooltipText={config.menuTooltip}
                                            tooltipDir="bottom"
                                            checked={config.value}
                                            disabled={config.disabled}
                                            onBlur={config.onClick}
                                            stopPropagation
                                        />
                                    </div>
                                )
                        )
                    case 'menu':
                        return (
                            <Menu
                                cssClass="sub-menu"
                                list={config.list}
                                width="400px"
                                dir="left"
                                disabled={config.disabled}
                                showTick
                                hasUniqueLast
                            >
                                <div className={`option-line ${config.disabled ? 'disabled' : ''}`}>
                                    <i className={config.icon} />
                                    <p dangerouslySetInnerHTML={{ __html: i18n.t(config.label) }} />
                                    {config.menuTooltip
                                        && (
                                            <HoverInfo
                                                cssClass="hover-info"
                                                title={config.menuTooltipTitle}
                                                desc={config.menuTooltip}
                                                dir="bottom"
                                            >
                                                <i className="info-icon fa-solid fa-circle-question" />
                                            </HoverInfo>
                                        )}
                                </div>
                            </Menu>
                        )
                    default:
                        return (
                            <div className="question-item">
                                <div
                                    className={`option-line ${config.disabled ? 'disabled' : ''}`}
                                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                    onClick={config.disabled ? null : config.onClick}
                                >
                                    <div className="option-line">
                                        <i className={config.icon} />
                                        <p dangerouslySetInnerHTML={{ __html: i18n.t(config.label) }} />
                                        {config.menuTooltip && (
                                            <HoverInfo
                                                cssClass="hover-info"
                                                title={config.menuTooltipTitle}
                                                desc={config.menuTooltip}
                                                dir="bottom"
                                            >
                                                <i className="info-icon fa-solid fa-circle-question" />
                                            </HoverInfo>
                                        )}
                                    </div>

                                    {config.rapidDisabled && (
                                        <i className="far fa-lock-keyhole" style={{ fontSize: 16 }} />
                                    )}
                                </div>
                            </div>
                        )
                }
            })
        )
    }

    getMandatoryOrRecommendedIcons = () => {
        const { recommended, mandatory } = this.qListInfo
        const { isMandatory } = mandatory
        const { isRecommended } = recommended
        let tooltip; let icon

        if (isMandatory) {
            tooltip = mandatory.menuTooltipText
            icon = 'fa-light fa-shield-check'
        } else if (isRecommended) {
            tooltip = recommended.menuTooltipText
            icon = 'fa-light fa-thumbs-up'
        }

        if (!isMandatory && !isRecommended) return null
        return (
            <div className="icon-tooltip">
                <Tooltip
                    text={tooltip}
                    direction="up"
                    style={{ width: 200 }}
                />
                <i className={icon} />
            </div>
        )
    }

    getAiParentInfo = () => {
        let aiParentId = null
        let isAiParentInSurvey = null
        if (process.env.AI_QUESTION) {
            const { store, question } = this.props
            const surveyQuestionIds = this.getAllSurveyQuestionIds
            aiParentId = store?.surveyAiQuestionMapping?.get(question.questionId.toLowerCase())
            isAiParentInSurvey = surveyQuestionIds.includes(aiParentId)
        }
        return { aiParentId, isAiParentInSurvey }
    }

    removeAiParent = () => {
        const { store } = this.props
        const { aiParentId } = this.getAiParentInfo()
        const allStandardPages = store.survey.pages.filter((p) => p.questions !== undefined)
        const { pageContainingAiParent, aiParentQuestion } = allStandardPages.reduce((acc, page) => {
            if (acc.stop) return acc
            const question = page.questions.find((pq) => pq.questionId === aiParentId)
            if (question) {
                return {
                    stop: true,
                    pageContainingAiParent: page,
                    aiParentQuestion: question,
                }
            }
            return acc
        }, { stop: false, pageContainingAiParent: null, aiParentQuestion: null })

        if (!aiParentQuestion) {
            store.addNotification(null, 'Could not find parent AI question', 'error')
            return
        }
        store.aiQuestions.remove = () => pageContainingAiParent.removeQuestion(
            {
                question: aiParentQuestion,
                index: pageContainingAiParent.questions.findIndex((pq) => pq.questionId === aiParentId),
                store,
            },
            true,
        )
        store.aiQuestions.removeModalOpen = true
    }

    addAiParent = async () => {
        const { store, question, page } = this.props
        const { survey } = store
        const parentAiQuestionId = store.surveyAiQuestionMapping?.get(question.questionId.toLowerCase())
        let parentAiQuestion
        try {
            parentAiQuestion = await getQuestionById(parentAiQuestionId, store)
            if (!parentAiQuestion) throw new Error()
        } catch (error) {
            store.addNotification(null, 'Could not find parent AI question', 'error')
            return
        }
        store.aiQuestions.add = () => {
            page.addQuestion(
                {
                    question: {
                        questionId: parentAiQuestionId,
                        mandatory: parentAiQuestion.metadata.isMandatory,
                        oid: parentAiQuestion.metadata.oid,
                    },
                    survey,
                    position: question.priority,
                    store,
                    isHidden: true,
                },
                true,
            )
        }
        store.aiQuestions.addModalOpen = true
    }

    renderGrabIcon = (isEditable, asDivs = false) => {
        let el = null
        if (asDivs) {
            el = (
                <div className="grab-icon">
                    <div/><div/><div/><div/><div/><div/><div/><div/>
                </div>
            )
        } else if (isEditable) {
            el = <img src={GrabIcon} alt="Grab icon" />
        } else {
            el = (
                <div className="icon-tooltip" style={{ paddingTop: '8px' }}>
                    <Tooltip
                        text={i18n.t('s_alt_q_islocked')}
                        direction="up"
                        style={{ width: 200 }}
                    />
                    <i className="fa-light fa-lock" />
                </div>
            )
        }
        return el
    }

    renderLeftIcons = (question) => {
        if (!question) return null
        const icons = []

        if (question.fetchFailed
            && question.question
            && !['ST', 'UV'].includes(question.question.questionTypeCode)
        ) {
            icons.push(
                <i key="iconFailed" className="fa-light fa-circle-star" style={{ fontSize: 18 }} />,
            )
        }

        icons.push(<Fragment key="iconMandatoryRecommended">{this.getMandatoryOrRecommendedIcons()}</Fragment>)

        if (question.usedInOtherSurveys) {
            icons.push(
                <div key="iconLink" className="icon-tooltip">
                    <Tooltip
                        text={i18n.t('s_alt_q_linked')}
                        direction="up"
                        style={{ width: 200 }}
                    />
                    <i className="fa-light fa-link" />
                </div>,
            )
        }

        if (question.hasPrepopulatedData) {
            icons.push(
                <div key="iconPrepopulated" className="icon-tooltip">
                    <Tooltip
                        text={i18n.t('s_alt_q_contains_reg_data')}
                        direction="up"
                        style={{ width: 200 }}
                    />
                    <i className="fa-light fa-upload" />
                </div>,
            )
        }

        if (this.getAiParentInfo().isAiParentInSurvey) {
            icons.push(
                <div key="iconAI" className="icon-tooltip">
                    <Tooltip
                        text={i18n.t('qlist.isAI.title.active')}
                        direction="up"
                        style={{ width: 200 }}
                    />
                    <i className="fa-light fa-sparkles" />
                </div>,
            )
        }
        return icons
    }

    renderRightIcons = (question) => {
        if (!question) return null
        const icons = []

        {/* the "isMandatory" below means "required to answer" */}
        if (question.isMandatory) {
            icons.push(
                <div className="icon-tooltip">
                    <Tooltip
                        text={i18n.t('questionLibrary.question.option.tooltip.makeRequired')}
                        direction="up"
                        style={{ width: 200 }}
                    />
                    <Typography type="p" color="grey">{i18n.t('s_required')}</Typography>
                </div>,
            )
        }
        return icons
    }

    renderQuestionItemFull = () => {
        const {
            question,
            store,
            provided,
            titleContent,
            oid,
            edit,
            page,
        } = this.props
        const className = (!question.modifiable ? ' locked' : '')
        const menuOptions = this.getMenu()
        const isHidden = question.isHidden || page.isHidden
        const { isRapidSurvey } = store.survey.metadata

        const hasPageLogic = store.survey.skipLogic.find((sl) => (sl.action.kind === 'SkipToPage' && R.pathOr(null, ['triggerGroups', 0, 'children', 0, 'triggers', 0, 'condition', 'pageQuestionId'], sl) === R.pathOr('', ['id'], question)))
        const hasDisplayLogic = question.hasSkipLogic(store) && question.hasSkipLogic(store).action.kind === 'ShowQuestion'
        const hasInheritedOptions = question.parentId && !question.parentId.match(/^[0|-]*$/)
        const hasMappings = question.hasPopulateFrom
        const { aiParentId, isAiParentInSurvey } = this.getAiParentInfo()

        const showQuestionSettingsBar = (
            hasPageLogic
            || hasDisplayLogic
            || hasInheritedOptions
            || question.hasOptionSkipLogic
            || hasMappings
            || isAiParentInSurvey
        )
        // exploriAdmins can interact with closed surveys only to map responses
        const hide3DotMenu = store?.survey.closed && !this.getMenuItemConfig(menuActions.MAP, question).isVisible

        return (
            <div
                className={`question-list-item-presentation ${showQuestionSettingsBar ? 'has-footer' : ''}`}
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
            >
                <div className={`survey-builder-question-list-item-header-presentation ${store.showDebug ? 'show-debug' : ''}`}>
                    <div className={`question-list-item-left-presentation q_${oid}`} />
                    <div className="grab-container">
                        {this.renderGrabIcon(question.canBeEdited)}
                    </div>
                    <div className="question-list-item-right-presentation">
                        <div className={`
                            question-list-item-right-top-presentation
                            ${isHidden ? 'isHidden' : ''}
                            ${this.open ? 'expanded' : ''}`}
                        >
                            <span className={`survey-builder-question-header-row ${className}`}>
                                <div className="survey-builder-icons-container title-container">
                                    {this.renderLeftIcons(question)}

                                    <div className="title-inner-container" onClick={this.togglePreview}>
                                        <Typography type="h4" cssClass={isHidden ? 'isHidden' : ''}>
                                            {titleContent}
                                        </Typography>
                                        {isHidden && (
                                            <div className="icon-tooltip">
                                                <Tooltip
                                                    text={i18n.t('s_alt_q_isHidden')}
                                                    direction="up"
                                                    style={{ width: 200 }}
                                                />
                                                <i className="fa-light fa-eye-slash" style={{ color: 'grey' }} />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="survey-builder-icons-container">
                                    {this.renderRightIcons(question)}

                                    { aiParentId && !isAiParentInSurvey && (
                                        <Button
                                            label={i18n.t('s_add_ai')}
                                            cssClass={`question-btn add-ai-btn ${store.survey.closed ? 'disabled' : ''}`}
                                            onClick={this.addAiParent}
                                            icon="fal fa-sparkles"
                                            disabled={store.survey.closed
                                        }>
                                            <HoverInfo
                                                cssClass="hover-info"
                                                desc={i18n.t('s_ai_categorisation_tooltip_add')}
                                                dir="bottom-left"
                                            >
                                                <i className="fas fa-circle-question" />
                                            </HoverInfo>
                                        </Button>
                                    )}

                                    {!this.getMenuItemConfig(menuActions.EDIT, question).isDisabled && !isRapidSurvey && (
                                        <Button
                                            label={i18n.t('edit')}
                                            cssClass="question-btn"
                                            onClick={edit}
                                        />
                                    )}

                                    <div className={`options-container options-handler-container ${this.menuOpen ? 'focused' : ''}`} onClick={this.toggleMenu}>
                                        {hide3DotMenu ? (
                                            null
                                        ) : (
                                            <Menu
                                                cssClass="option-menu"
                                                list={menuOptions}
                                                minWidth="375px"
                                                dir="left"
                                                hasUniqueLast={this.getMenuItemConfig(menuActions.DELETE, question).isVisible}
                                            >
                                                <div className="menu-icon">
                                                    <i className="fa-regular fa-ellipsis-vertical" />
                                                </div>
                                            </Menu>
                                        )}
                                    </div>
                                </div>
                            </span>
                        </div>
                        <div>
                            {showQuestionSettingsBar
                                && (
                                    <div className={`question-list-item-right-bottom-presentation ${this.open ? 'expanded' : ''}`}>
                                        <div className="question-preview-presentation">
                                            {hasPageLogic
                                                ? (
                                                        <div 
                                                            className={`survey-builder-tab ${this.openTab === 'page' ? 'active' : ''}`}
                                                            onClick={() => this.setOpenTab('page')}
                                                        >
                                                            <span className={this.openTab === 'page' ? 'expanded' : ''}>{i18n.t('pageLogic')}</span>
                                                        </div>
                                                ) : null}

                                            {hasDisplayLogic
                                                ? (
                                                    <div 
                                                        className={`survey-builder-tab ${this.openTab === 'question' ? 'active' : ''}`}
                                                        onClick={() => this.setOpenTab('question')}
                                                    >
                                                        <span className={this.openTab === 'question' ? 'expanded' : ''}>{i18n.t('displayLogic')}</span>
                                                    </div>
                                                ) : null}

                                            {hasInheritedOptions
                                                ? (
                                                    <div 
                                                        className={`survey-builder-tab ${this.openTab === 'inherited' ? 'active' : ''}`}
                                                        onClick={() => this.setOpenTab('inherited')}
                                                    >
                                                        <span className={this.openTab === 'inherited' ? 'expanded' : ''}>{i18n.t('s_alt_inherited_options')}</span>
                                                    </div>
                                                ) : null}

                                            {question.hasOptionSkipLogic
                                                ? (
                                                    <div 
                                                        className={`survey-builder-tab ${this.openTab === 'option' ? 'active' : ''}`}
                                                        onClick={() => this.setOpenTab('option')}
                                                    >
                                                        <span className={this.openTab === 'option' ? 'expanded' : ''}>{i18n.t('s_alt_option_logic')}</span>
                                                    </div>
                                                ) : null}
                                            {hasMappings
                                                ? (
                                                    <div 
                                                        className={`survey-builder-tab ${this.openTab === 'mapping' ? 'active' : ''}`}
                                                        onClick={() => this.setOpenTab('mapping')}
                                                    >
                                                        <span className={this.openTab === 'mapping' ? 'expanded' : ''}>{i18n.t('s_alt_q_isMapped')}</span>
                                                    </div>
                                                ) : null}
                                            {isAiParentInSurvey
                                                ? (
                                                    <div 
                                                        className={`survey-builder-tab ${this.openTab === 'aiMapping' ? 'active' : ''}`}
                                                        onClick={() => this.setOpenTab('aiMapping')}
                                                    >
                                                        <span className={this.openTab === 'aiMapping' ? 'expanded' : ''}>{i18n.t('s_ai_categorisation')}</span>
                                                    </div>
                                                ) : null}


                                        </div>
                                    </div>
                                )}
                            <AnimateHeight
                                duration={250}
                                height={this.open ? 'auto' : 0}
                                className={this.open ? 'question-content' : ''}
                            >
                                <div className="question-content-preview border-style">
                                    {this.renderContent()}
                                </div>
                            </AnimateHeight>
                        </div>
                    </div>
                </div>
                <DebugInfo
                    store={store}
                    hideDataLink
                    hideCloneLink
                    questionTypeCode={question?.question?.questionTypeCode}
                    questionId={question.questionId}
                    pageQuestionId={question.id}
                />
            </div>
        )
    }

    renderQuestionItemFailed = () => {
        const {
            question,
            titleContent,
            className,
            reloadQuestion,
            provided,
        } = this.props

        const { questionoid } = question

        return (
            <div
                className={`question-list-item-presentation ${className}`}
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
            >
                <div className="survey-builder-question-list-item-header-presentation failed">
                    <div className={`question-list-item-left-presentation q_${questionoid}`} />
                    <div className="question-list-item-right-presentation">
                        <div className="question-list-item-right-top-presentation failed">
                            <span className={`survey-builder-question-header-row ${className}`}>
                                <div className="survey-builder-icons-container">
                                    <i className="fal fa-exclamation-triangle" aria-hidden="true" />
                                    {titleContent}
                                </div>
                                <div className="survey-builder-icons-container">
                                    <i className="far fa-sync" onClick={reloadQuestion} title={i18n.t('s_alt_q_reload')} />
                                </div>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    renderQuestionItemLite = () => {
        const {
            question,
            titleContent,
            className,
            onClick,
            addOptionButton,
        } = this.props

        const { questionoid } = question

        return (
            <div>
                <div className={`question-list-item-presentation ${className}`}>
                    <div className="survey-builder-question-list-item-header-presentation lite">
                        <div className={`question-list-item-left-presentation q_${questionoid}`} />
                        <div className="question-list-item-right-presentation">
                            <div className="question-list-item-right-top-presentation">
                                <div
                                    className={`chevron-container ${this.open ? 'expanded' : ''}`}
                                    style={{ marginInline: 10 }}
                                    onClick={onClick}
                                >
                                    <i className={`fa ${this.open ? 'fa-chevron-up' : 'fa-chevron-down'}`} aria-hidden="true" />
                                </div>
                                <span className={`survey-builder-question-header-row ${className}`}>
                                    <div className="survey-builder-icons-container">
                                        {question.usedInOtherSurveys && (
                                            <i className="fa-light fa-link" />
                                        )}
                                        {titleContent}
                                    </div>
                                    {addOptionButton()}
                                </span>
                            </div>
                        </div>
                    </div>
                    <AnimateHeight
                        duration={250}
                        height={this.open ? 'auto' : 0}
                        className={this.open ? 'question-content' : ''}
                    >
                        <div className="question-content-preview border-style">
                            {this.renderContent()}
                        </div>
                    </AnimateHeight>
                </div>
            </div>
        )
    }

    renderAIQuestionItem = () => {
        const {
            provided,
            titleContent,
            question,
            store,
        } = this.props
        return (
            <div
                className="question-list-item-presentation ai-parent"
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
            >
                <div className="survey-builder-question-list-item-header-presentation">
                    <div className="grab-container">
                        {this.renderGrabIcon(true, true)}
                    </div>
                    <div className="question-list-item-right-presentation">
                        <div className="question-list-item-right-top-presentation">
                            <span className="survey-builder-question-header-row">
                                <div className="survey-builder-icons-container title-container">
                                    <div className="icon-tooltip">
                                        <Tooltip
                                            text={i18n.t('qlist.isAI.title.active')}
                                            direction="up"
                                            style={{ width: 200 }}
                                        />
                                        <i className="fa-light fa-sparkles" />
                                    </div>
                                    <div>
                                        <Typography type="b">
                                            {titleContent}
                                        </Typography>
                                        <Typography type="tiny">
                                            <div className="question-title-container">
                                                <span>{i18n.t('s_ai_categorisation')}</span>
                                                <div className="ai-icon-tooltip">
                                                    <Tooltip
                                                        text={i18n.t('s_ai_categorisation_tooltip')}
                                                        direction="bottom"
                                                        isNew
                                                    >
                                                        <i className="fas fa-circle-question" />
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        </Typography>
                                    </div>
                                </div>
                            </span>
                        </div>
                    </div>
                </div>
                <DebugInfo
                    store={store}
                    hideDataLink
                    hideCloneLink
                    questionTypeCode={question?.question?.questionTypeCode}
                    questionId={question.questionId}
                    pageQuestionId={question.id}
                />
            </div>
        )
    }

    render() {
        const { isLite, isFailed, question } = this.props
        let render = this.renderQuestionItemFull
        if (isLite) {
            render = this.renderQuestionItemLite
        } else if (isFailed) {
            render = this.renderQuestionItemFailed
        } else if (this.isAiParent && question.isHidden) {
            render = this.renderAIQuestionItem
        }
        return (
            <Fragment>
                {render()}
            </Fragment>
        )
    }
}
